/**
 * contact-form-capture.js — mirrors every Shopify storefront contact-form
 * submission to the dealer-portal so they show up in the daily digest +
 * any future CRM hook. Backwards-compatible: Shopify's own form action
 * still fires and enquiry@ still receives the notification email; this
 * runs in parallel as a sniff.
 *
 * Loaded by snippets/contact-form-capture.liquid via {{ 'contact-form-capture.js' | asset_url | script_tag }}
 *
 * Listener target: every <form action="/contact#…"> on the page (Shopify's
 * `{% form 'contact' %}` Liquid tag emits this exact action). One global
 * delegated listener so we don't have to retrofit each section/snippet.
 */
(function () {
  'use strict';

  var CFG = window.RC_CONTACT_CONFIG || {};
  var STORE = CFG.store || 'AU';
  var API_BASE = CFG.apiBase || 'https://api.rolacase.us';
  var API_KEY = CFG.apiKey || 'rola-dp-7f3k9x2m4p';
  var ENDPOINT = API_BASE + '/api/v1/contact-form';

  function isShopifyContactForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = (form.getAttribute('action') || '').toLowerCase();
    return action.indexOf('/contact') === 0 || action.indexOf('/contact#') >= 0;
  }

  function pickField(form, names) {
    // Case-INSENSITIVE on the field name so locale-cased variants
    // (contact[Message], contact[Name], contact[Body]) are matched — otherwise
    // the message silently dropped once the native Shopify submit is gone.
    var lowered = names.map(function (n) { return n.toLowerCase(); });
    var all = form.querySelectorAll('[name]');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      var nm = (el.getAttribute('name') || '').toLowerCase();
      if (lowered.indexOf(nm) === -1) continue;
      if (typeof el.value === 'string' && el.value.trim() !== '') return el.value.trim();
    }
    return '';
  }

  // collectExtraContactFields scans every contact[*] field on the form,
  // skips the ones picked by name above, and returns the rest as a flat
  // map. Catches form-specific fields (address, cart_data, gallery_image,
  // etc.) without having to enumerate every theme form variant. Sophia
  // 2026-05-15: Simon Brown filled the quote-request form with selected
  // products + delivery address but the team email arrived empty because
  // our interceptor only grabbed the canonical 8 fields.
  var KNOWN_CONTACT_KEYS = (function () {
    var set = {};
    [
      'firstname', 'first_name', 'lastname', 'last_name', 'name',
      'email', 'phone', 'countrycode', 'country', 'country_code',
      'postcode', 'zip', 'body', 'message',
      'form_type'
    ].forEach(function (k) { set[k.toLowerCase()] = true; });
    return set;
  })();

  function collectExtraContactFields(form) {
    var extras = {};
    // Include <select> (e.g. the "What can we help you with?" enquiry-type
    // dropdown) — previously only input/textarea were scanned, so the select's
    // answer never reached our API / the lead email.
    var fields = form.querySelectorAll('input[name^="contact["], textarea[name^="contact["], select[name^="contact["]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      var nameAttr = el.name || '';
      var match = nameAttr.match(/^contact\[([^\]]+)\]$/);
      if (!match) continue;
      var keyRaw = match[1];
      var keyLower = keyRaw.toLowerCase();
      if (KNOWN_CONTACT_KEYS[keyLower]) continue;
      var val;
      if (el.tagName === 'SELECT') {
        // Capture the readable option label ("Order Tracking"), not the raw
        // value ("order-tracking").
        var opt = (el.selectedIndex >= 0) ? el.options[el.selectedIndex] : null;
        val = (opt && typeof opt.text === 'string') ? opt.text : (el.value || '');
      } else {
        val = (typeof el.value === 'string') ? el.value : '';
      }
      if (val.length === 0) continue;
      // Cap per-field length to 20KB so a malicious or runaway hidden
      // input doesn't balloon the payload. cart_data is normally <5KB.
      if (val.length > 20000) val = val.slice(0, 20000) + '… [truncated]';
      extras[keyLower] = val;
    }
    return extras;
  }

  function buildPayload(form) {
    var payload = {
      store: STORE,
      first_name: pickField(form, ['contact[FirstName]', 'contact[first_name]', 'first_name']),
      last_name:  pickField(form, ['contact[LastName]', 'contact[last_name]', 'last_name']),
      name:       pickField(form, ['contact[Name]', 'contact[name]', 'name']),
      email:      pickField(form, ['contact[email]', 'email']),
      phone:      pickField(form, ['contact[phone]', 'contact[Phone]', 'phone']),
      country_code: pickField(form, ['contact[CountryCode]', 'contact[country]', 'country_code']),
      postcode:   pickField(form, ['contact[postcode]', 'contact[Postcode]', 'contact[zip]', 'postcode']),
      message:    pickField(form, ['contact[Body]', 'contact[body]', 'contact[Message]', 'contact[message]', 'message']),
      // referrer = the previous page (e.g. where they clicked the link from)
      // submit_page = the page where they actually submitted the form
      // Both useful: referrer answers "what marketing channel?", submit_page
      // answers "which page on the site converted this lead?"
      referrer:   (typeof document !== 'undefined' ? document.referrer : '') || '',
      submit_page: (typeof window !== 'undefined' && window.location) ? window.location.href : ''
    };
    // extra_fields carries every other contact[*] field the form sends
    // (address, cart_data, gallery_caption, gallery_source, etc.). Backend
    // stores them in Raw JSON; renderer surfaces them in the email.
    var extras = collectExtraContactFields(form);
    if (Object.keys(extras).length > 0) {
      payload.extra_fields = extras;
    }
    // Cloudflare Turnstile proof-of-browser token (if the widget rendered one).
    // Turnstile injects a hidden input[name="cf-turnstile-response"] into the
    // form on success; forward it so the dealer-portal can verify server-side.
    try {
      var ts = form.querySelector('[name="cf-turnstile-response"]');
      if (ts && ts.value) { payload.cf_turnstile_response = ts.value; }
    } catch (_) {}
    return payload;
  }

  // collectFiles walks the form AND document for any <input type="file"> with
  // a selected file. Some sections (e.g. contact-card.liquid) render the
  // file input OUTSIDE the form (inside a sibling label with id="rc-file")
  // and rely on a custom JS layer to ferry the file along. To stay
  // resilient against that pattern, we scan the form first (the cheap
  // common case) and then scan the entire document for orphan file inputs
  // the user has actually selected files into. False-positive risk is
  // negligible: a file input only contributes if the user actively chose
  // a file, so an unrelated form on the same page won't accidentally upload.
  function collectFiles(form) {
    var out = [];
    var seen = new Set();
    var addFiles = function (inp) {
      if (seen.has(inp)) return;
      seen.add(inp);
      if (inp.files && inp.files.length) {
        for (var j = 0; j < inp.files.length; j++) {
          out.push(inp.files[j]);
        }
      }
    };
    var inForm = form.querySelectorAll('input[type="file"]');
    for (var i = 0; i < inForm.length; i++) addFiles(inForm[i]);
    // Document-wide scan for orphan file inputs the user has filled
    var anywhere = document.querySelectorAll('input[type="file"]');
    for (var k = 0; k < anywhere.length; k++) addFiles(anywhere[k]);
    return out;
  }

  // sumSize totals the bytes of all selected files. Used to refuse uploads
  // that would push the resulting email past Gmail's ~25MB cap and avoid
  // multipart parsing OOM on the server. We cap at 12MB on our side which
  // leaves headroom after base64 encoding (~33% inflation in MIME).
  function sumSize(files) {
    var n = 0;
    for (var i = 0; i < files.length; i++) n += files[i].size || 0;
    return n;
  }

  // recentSubmits is the in-memory dedup gate for layer 2. Keys are a hash
  // of email + name + message; values are timestamps. If the same key has
  // been seen within DEDUP_WINDOW_MS, we skip the fetch. Caught Antonello's
  // double-tap on 2026-05-15. The backend has its own dedup layer too —
  // this one stops the wasted HTTP round-trip when the duplicate is purely
  // client-side (rapid taps before button could be disabled).
  var recentSubmits = {};
  var DEDUP_WINDOW_MS = 90 * 1000;

  function dedupKey(payload) {
    return [
      String(payload.email || ''),
      String(payload.name || ''),
      String(payload.first_name || ''),
      String(payload.last_name || ''),
      String(payload.message || ''),
    ].join('|').slice(0, 500);
  }

  function isRecentDuplicate(key) {
    var now = Date.now();
    var seen = recentSubmits[key];
    if (seen && (now - seen) < DEDUP_WINDOW_MS) return true;
    recentSubmits[key] = now;
    // Garbage-collect old keys so the map doesn't grow forever in a
    // long-lived SPA session.
    for (var k in recentSubmits) {
      if ((now - recentSubmits[k]) > DEDUP_WINDOW_MS * 2) delete recentSubmits[k];
    }
    return false;
  }

  // submitWithRetry POSTs once; on network error OR non-2xx response,
  // retries once after RETRY_DELAY_MS. Both attempts are fire-and-forget
  // from the caller's POV. Logs to console.warn on failure so a
  // returning customer can be diagnosed in browser dev tools.
  //
  // Why this exists: dealer-portal container can cold-start for up to
  // 58s (ghcr image-pull then docker compose recreate). A customer who
  // submits in those first 30s has their fetch() fail silently because
  // the HTTP listener isn't bound yet. Single retry at 3s catches the
  // tail of that window — empirically the listener is up in <30s, so a
  // retry near second-3 has high success probability against the cold
  // start.
  var RETRY_DELAY_MS = 3000;

  function postOnce(url, body, headers, useKeepalive) {
    return fetch(url, {
      method: 'POST',
      keepalive: useKeepalive,
      credentials: 'omit',
      headers: headers,
      body: body
    }).then(function (resp) {
      if (!resp.ok) {
        // Non-2xx — treat as failure for retry purposes.
        var err = new Error('contact-capture: non-2xx response status=' + resp.status);
        err.status = resp.status;
        throw err;
      }
      return resp;
    });
  }

  function submitWithRetry(url, body, headers, useKeepalive) {
    postOnce(url, body, headers, useKeepalive).catch(function (firstErr) {
      try { console && console.warn && console.warn('[contact-capture] first attempt failed, retrying in', RETRY_DELAY_MS, 'ms:', firstErr && firstErr.message || firstErr); } catch (_) {}
      setTimeout(function () {
        postOnce(url, body, headers, useKeepalive).catch(function (secondErr) {
          try { console && console.warn && console.warn('[contact-capture] retry failed, giving up:', secondErr && secondErr.message || secondErr); } catch (_) {}
        });
      }, RETRY_DELAY_MS);
    });
  }

  // --- API-PRIMARY path (awaitable) -------------------------------------------
  // submitPrimary returns Promise<boolean>. true => OUR API confirmed a real-lead
  // capture (the caller shows success and Shopify is never touched, so the
  // /challenge puzzle never appears). false => the caller MUST fall back to the
  // native Shopify /contact submit so the lead is never lost. NEVER throws.
  var PRIMARY_TIMEOUT_MS = 7000; // hard cap so a cold-start connect-stall fails fast -> Shopify fallback

  // Form-scoped file collector — only scans inputs INSIDE this form (unlike the
  // document-wide collectFiles), so a file staged elsewhere on the page (e.g. the
  // chat widget's #rc-file) can never bleed into a contact submit.
  function collectFormFiles(form) {
    var out = [];
    try {
      var inForm = form.querySelectorAll('input[type="file"]');
      for (var i = 0; i < inForm.length; i++) {
        var inp = inForm[i];
        if (inp.files && inp.files.length) {
          for (var j = 0; j < inp.files.length; j++) out.push(inp.files[j]);
        }
      }
    } catch (_) {}
    return out;
  }

  // GA4/GTM conversion signal. Fired ONCE per confirmed real-lead capture, from
  // the single success point in submitPrimary so EVERY form (contact-card, the
  // globally-handled forms, custom-gallery-unlimited) tracks identically. This
  // replaces two now-defunct paths: (1) the legacy GTM "URL-match" trigger that
  // fired on the post-submit ?contact_posted=true navigation (the API-primary
  // flow no longer navigates), and (2) the per-form inline dataLayer pushes that
  // the global capture handler's stopPropagation neutralises. Pushes the GA4
  // standard `generate_lead`; quote-context forms also push `send_quote_request`
  // (Google Ads Smart Bidding signal) to preserve the prior behaviour.
  function fireLeadConversion(form) {
    try {
      var src = (form && form.getAttribute && (form.getAttribute('id') || form.getAttribute('data-lead-source'))) || 'contact_form';
      var path = (location && location.pathname) || '';
      var isQuote = /quote/i.test(src) || /quote/i.test(path);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'generate_lead', lead_source: src, page_path: path });
      if (isQuote) {
        window.dataLayer.push({ event: 'send_quote_request', quote_source: src, page_path: path });
      }
      if (window.RolacaseTrack && typeof window.RolacaseTrack.track === 'function') {
        window.RolacaseTrack.track('quote_submit', { search_query: src, raw: { page_path: path } });
      }
    } catch (_) {}
  }

  function submitPrimary(form) {
    return new Promise(function (resolve) {
      // Outcome annotation for the caller's native-fallback decision (2026-07-29
      // desktop lead-loss fix). Default 'held' = show please-call, NO native
      // fallback — covers every early resolve(false) (bad payload, oversize, etc).
      // Only a confirmed capture upgrades to 'ok'; only a genuine client-side
      // TRANSPORT failure (fetch threw a non-Abort error = request never reached
      // the server, e.g. blocked by an ad-blocker/proxy) upgrades to 'transport',
      // which is the ONLY outcome that triggers the zero-duplicate native submit.
      form.__rcOutcome = 'held';
      var payload;
      try { payload = buildPayload(form); }
      catch (e) { resolve(false); return; }

      var files = collectFormFiles(form);
      var MAX = 12 * 1024 * 1024;
      if (files.length && sumSize(files) > MAX) files = []; // oversize -> drop files, still try the lead

      var body, headers;
      try {
        if (files.length) {
          var fd = new FormData();
          for (var k in payload) {
            if (Object.prototype.hasOwnProperty.call(payload, k) && payload[k] != null && payload[k] !== '') {
              fd.append(k, String(payload[k]));
            }
          }
          for (var i = 0; i < files.length; i++) {
            fd.append('attachments', files[i], files[i].name || ('attachment-' + (i + 1)));
          }
          body = fd; headers = { 'X-API-Key': API_KEY };
        } else {
          body = JSON.stringify(payload);
          headers = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };
        }
      } catch (e) { resolve(false); return; }

      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timer = setTimeout(function () { try { controller && controller.abort(); } catch (_) {} }, PRIMARY_TIMEOUT_MS);

      fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'omit',
        headers: headers,
        body: body,
        signal: controller ? controller.signal : undefined
      }).then(function (resp) {
        if (!resp.ok) { clearTimeout(timer); resolve(false); return null; } // network 5xx/4xx -> fallback
        return resp.json().catch(function () { return null; }).then(function (json) {
          clearTimeout(timer);
          // SUCCESS CONTRACT: any 2xx AND ok===true AND id is a NON-EMPTY string.
          // id==='' means shadow-accepted/quarantined -> NOT a confirmed real-lead
          // capture -> fall back to Shopify so the lead is never lost.
          var ok = !!(json && json.ok === true && typeof json.id === 'string' && json.id !== '');
          if (ok) { form.__rcOutcome = 'ok'; fireLeadConversion(form); } // GA4/GTM conversion on a confirmed capture
          // id==='' (shadow-quarantine) and non-2xx stay 'held' -> please-call,
          // no native fallback (backend already holds it -> would duplicate).
          resolve(ok);
        });
      }).catch(function (err) {
        clearTimeout(timer);
        // AbortError = our own PRIMARY_TIMEOUT_MS abort; the request may have
        // reached + committed server-side after we gave up -> keep 'held' (no
        // native fallback, else risk a duplicate). Any OTHER throw (blocked by
        // an extension/proxy/DNS, connection refused, TypeError 'Failed to
        // fetch') means the POST never reached the server -> 'transport' so the
        // caller safely recovers the lead via a native /contact submit.
        if (!err || err.name !== 'AbortError') { form.__rcOutcome = 'transport'; }
        resolve(false);
      });

      // Identify side-effect (best-effort, never blocks the resolve).
      try {
        if (payload.email && window.RolacaseTrack && window.RolacaseTrack.identify) window.RolacaseTrack.identify(payload.email);
        if (window.RolacaseTrack && window.RolacaseTrack.track) window.RolacaseTrack.track('contact_form', { email: payload.email || '' });
      } catch (_) {}
    });
  }

  window.RolacaseContactCapture = window.RolacaseContactCapture || {};
  window.RolacaseContactCapture.submitPrimary = submitPrimary;

  // --- Shared API-primary helpers (used by the global handler + per-file owned
  // handlers). Centralised so every contact form behaves identically. ----------

  // Mint a FRESH Turnstile token at submit time (closes the stale-token race that
  // would let ENFORCE quarantine a real human). Resolves when the token input is
  // populated, or after a ~2.5s cap so we never hang the submit.
  function refreshTurnstileToken(form) {
    return new Promise(function (resolve) {
      try {
        var idAttr = form.getAttribute('data-rc-turnstile-id');
        if (!window.turnstile || idAttr == null) { resolve(); return; }
        var done = false;
        var finish = function () { if (!done) { done = true; resolve(); } };
        try { window.turnstile.execute(idAttr); } catch (_) {}
        var tries = 0;
        var t = setInterval(function () {
          tries++;
          var inp = form.querySelector('[name="cf-turnstile-response"]');
          if ((inp && inp.value) || tries >= 25) { clearInterval(t); finish(); }
        }, 100);
      } catch (_) { resolve(); }
    });
  }

  // Uniform SUCCESS: hide the form, insert a banner, scroll into view. Owned
  // forms with a bespoke success UI pass their own onSuccess instead.
  function rcShowFormSuccess(form) {
    try {
      var banner = document.createElement('div');
      banner.className = 'rc-form-success';
      banner.setAttribute('role', 'status');
      banner.style.cssText = 'padding:16px 18px;margin:12px 0;border-radius:8px;background:#e8f5e9;color:#1b5e20;font-size:15px;line-height:1.5;';
      banner.textContent = 'Thanks, your enquiry is on its way. One of our team will be in touch shortly.';
      if (form.parentNode) form.parentNode.insertBefore(banner, form);
      form.style.display = 'none';
      banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {}
  }

  // Uniform FAILURE: please-call. Re-enables the submit control so the customer
  // can retry. Used on EVERY false/throw path.
  function rcShowCallMessage(form, submitBtn) {
    try {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.style.opacity = ''; submitBtn.style.cursor = ''; }
      var toast = form.querySelector('.rc-form-error');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'rc-form-error';
        toast.setAttribute('role', 'alert');
        toast.style.cssText = 'padding:14px 18px;margin:12px 0;border-radius:8px;background:#fdecea;color:#b71c1c;font-size:15px;line-height:1.5;';
        if (form.firstChild) form.insertBefore(toast, form.firstChild);
        else form.appendChild(toast);
      }
      toast.textContent = 'Sorry, we could not send your enquiry just now. Please call us on (02) 4340 1444, or try again shortly.';
      toast.style.display = 'block';
      toast.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {}
  }

  // rcNativeFallback — recover a lead the API POST could not deliver (2026-07-29
  // desktop lead-loss fix). Called ONLY on a 'transport' outcome (the API fetch
  // was blocked client-side and never reached the server), so there is no API
  // record and this native /contact POST cannot duplicate one. It emails the
  // store's enquiry@ address (accepting Shopify's hCaptcha on this path, which
  // beats silently losing the lead). Uses HTMLFormElement.prototype.submit.call
  // — the submit METHOD dispatches NO 'submit' event, so neither the delegated
  // capture-phase listener below nor the early hCaptcha guard re-intercept (no
  // loop). NEVER use requestSubmit() (it fires a submit event -> infinite loop).
  // One-shot guard so a stray re-entry can never double-POST.
  function rcNativeFallback(form) {
    try {
      if (form.__rcNativeFallbackDone) return;
      form.__rcNativeFallbackDone = true;
      // Count the recovered lead too, so Google Ads Smart Bidding sees the
      // ad-blocker/proxy/slow-network segment (the fallback path otherwise fired
      // NO conversion — the CPL A$75->A$243 mismeasurement class). Fire BEFORE
      // the native submit navigates. Mutually exclusive with the happy-path fire
      // (submitPrimary :346, ok===true) since this runs only when the API POST
      // failed, so there is no double-count.
      try { fireLeadConversion(form); } catch (_) {}
      HTMLFormElement.prototype.submit.call(form);
    } catch (e) {
      rcShowCallMessage(form, form.querySelector('button[type="submit"], input[type="submit"], [data-rc-submit]'));
    }
  }

  // runApiPrimary — the single API-primary flow. Reusable by per-file owned
  // handlers (esp. custom-gallery-unlimited's click path which fires no submit
  // event). in-flight guard + button disable + reportValidity + fresh Turnstile
  // + awaited submitPrimary. opts.onSuccess/onFailure override the uniform UI.
  function runApiPrimary(form, opts) {
    opts = opts || {};
    if (form.__rcInFlight) return Promise.resolve(false);
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
      return Promise.resolve(false); // HTML5 required/email validation
    }
    form.__rcInFlight = true;
    var submitBtn = form.querySelector('button[type="submit"], input[type="submit"], [data-rc-submit]');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.style.opacity = '0.6'; submitBtn.style.cursor = 'not-allowed'; }
    var fail = function () {
      form.__rcInFlight = false;
      if (opts.onFailure) { try { opts.onFailure(); } catch (_) {} if (submitBtn) { submitBtn.disabled = false; submitBtn.style.opacity = ''; submitBtn.style.cursor = ''; } return; }
      // No custom handler (standard delegated /contact forms + the gallery click
      // path): recover the lead. On a 'transport' failure (API blocked, never
      // reached the server) fall back to a native /contact POST — zero-duplicate
      // because no API record exists. Every other outcome ('held': quarantine,
      // timeout/abort, 4xx/5xx) stays please-call, since the server may already
      // hold the lead and a native submit could duplicate it.
      if (form.__rcOutcome === 'transport') { rcNativeFallback(form); return; }
      rcShowCallMessage(form, submitBtn);
    };
    return Promise.resolve()
      .then(function () { return refreshTurnstileToken(form); })
      .then(function () {
        if (!window.RolacaseContactCapture || typeof window.RolacaseContactCapture.submitPrimary !== 'function') { form.__rcOutcome = 'transport'; fail(); return false; } // layer not ready = no API request sent = safe native fallback
        return window.RolacaseContactCapture.submitPrimary(form).then(function (ok) {
          if (ok === true) {
            if (opts.onSuccess) { try { opts.onSuccess(); } catch (_) {} } else rcShowFormSuccess(form);
            return true;
          }
          fail(); return false;
        });
      })
      .catch(function () { fail(); return false; });
  }

  window.RolacaseContactCapture.refreshTurnstileToken = refreshTurnstileToken;
  window.RolacaseContactCapture.rcShowFormSuccess = rcShowFormSuccess;
  window.RolacaseContactCapture.rcShowCallMessage = rcShowCallMessage;
  window.RolacaseContactCapture.rcNativeFallback = rcNativeFallback;
  window.RolacaseContactCapture.runApiPrimary = runApiPrimary;

  function capture(form) {
    var payload;
    try {
      payload = buildPayload(form);
    } catch (err) {
      // Never let our sniff break the user's submit.
      try { console && console.warn && console.warn('[contact-capture] buildPayload failed:', err); } catch (_) {}
      return;
    }
    if (!payload.email && !payload.phone && !payload.message) {
      // Empty / honey-pot submission — Shopify itself will reject this,
      // we don't need to record it.
      return;
    }
    if (isRecentDuplicate(dedupKey(payload))) {
      // Rapid double-tap on SEND. Backend would catch it too, but skipping
      // the round-trip is cheaper.
      try { console && console.info && console.info('[contact-capture] duplicate submit suppressed'); } catch (_) {}
      return;
    }

    var files = [];
    try { files = collectFiles(form); } catch (_) {}
    var MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;
    if (files.length && sumSize(files) > MAX_ATTACHMENT_BYTES) {
      try { console && console.warn && console.warn('[contact-capture] attachments exceed 12MB, sending without files'); } catch (_) {}
      files = []; // drop files when too large — still capture the lead, just without the file
    }

    try {
      var body, headers;
      if (files.length) {
        // Multipart path: build FormData with text fields + file blobs.
        // Browser sets the Content-Type with boundary automatically when
        // body is a FormData instance — DO NOT set Content-Type manually
        // or the boundary won't be included.
        var fd = new FormData();
        for (var k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k) && payload[k] != null && payload[k] !== '') {
            fd.append(k, String(payload[k]));
          }
        }
        for (var i = 0; i < files.length; i++) {
          fd.append('attachments', files[i], files[i].name || ('attachment-' + (i + 1)));
        }
        body = fd;
        headers = { 'X-API-Key': API_KEY };
      } else {
        // JSON path (existing behaviour for forms with no files).
        body = JSON.stringify(payload);
        headers = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };
      }
      // keepalive: true lets the request finish even after the page navigates.
      // No await — Shopify's form submit continues to fire as normal.
      // NOTE: keepalive has a 64KB limit per spec, browsers WILL drop the
      // request if body exceeds it. Multipart with a real file is usually
      // well over 64KB, so we skip keepalive for multipart and accept that
      // page navigation could cut off the request — capture is still
      // best-effort, the user's Shopify submit is unaffected either way.
      //
      // Retry-on-failure (added 2026-05-27 after jono submission silently
      // dropped during 58s dealer-portal cold-start window). Single retry
      // after 3s catches the tail of the cold-start window. Detects both
      // network errors (.catch) AND non-2xx responses (resp.ok check).
      // Logs to console.warn on first failure so future investigations
      // have a browser-side trace even though Shopify's submit path
      // continues unaffected.
      submitWithRetry(ENDPOINT, body, headers, files.length === 0);
    } catch (err) {
      try { console && console.warn && console.warn('[contact-capture] fetch failed:', err); } catch (_) {}
    }
    // Hand the identified email to the storefront tracker so future
    // events on this browser join cookie_id → email automatically.
    try {
      if (payload.email && window.RolacaseTrack && window.RolacaseTrack.identify) {
        window.RolacaseTrack.identify(payload.email);
      }
      if (window.RolacaseTrack && window.RolacaseTrack.track) {
        window.RolacaseTrack.track('contact_form', { email: payload.email || '' });
      }
    } catch (_) {}
  }

  // Delegated listener (capture phase): API-PRIMARY for EVERY /contact form.
  // The native Shopify /contact POST is suppressed on the happy path (that POST
  // is what triggers Shopify's hCaptcha /challenge), so no puzzle in the common
  // case. EXCEPTION (2026-07-29 lead-loss fix): if the API POST is blocked
  // client-side and never reaches the server (a 'transport' outcome), fail()
  // falls back to a native /contact POST so the lead is never lost (accepting
  // the hCaptcha on that path). Delivery is awaited via submitPrimary.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!isShopifyContactForm(form)) return;
    // Owned forms drive their OWN runApiPrimary call (bespoke success UI or a
    // non-submit-event trigger). Yield entirely so we never double-handle.
    if (form.getAttribute('data-rc-primary-owned')) return;
    // Stop the native /contact submit (the hCaptcha trigger) + any stray bubble
    // listener that would re-show success or re-submit.
    e.preventDefault();
    e.stopPropagation();
    // Boot guard: if RC_CONTACT_CONFIG never rendered, CFG.store is unset (STORE
    // silently defaulted to 'AU') — do NOT risk mis-routing. Degrade to please-call.
    if (!CFG.store) { rcNativeFallback(form); return; } // config never rendered = no API request will be sent = zero-dup native delivery to enquiry@ (and avoids mis-routing)
    runApiPrimary(form); // uniform success banner / please-call
  }, true);

  // --- Bot defence-in-depth (the dealer-portal backend gate is the primary
  // defence; this hardens against a future page-crawling bot) -------------
  // Inject two fields into every Shopify contact form on the page:
  //   contact[website]        — a honeypot, hidden off-screen. Humans never
  //                             see or fill it; a naive form-filling bot does.
  //   contact[form_render_ts] — epoch-ms when the form was rendered, so the
  //                             backend can reject sub-second (machine) fills.
  // Both ride to the dealer-portal as contact[*] fields and are harvested by
  // collectExtraContactFields into extra_website / extra_form_render_ts. The
  // empty honeypot is dropped by the existing "val.length === 0" skip, so it
  // only travels when actually filled. Harmless to Shopify's native submit —
  // extra contact[*] fields it doesn't expect are simply ignored.
  function ensureBotFields(form) {
    if (!isShopifyContactForm(form) || form.getAttribute('data-rc-bot-fields')) return;
    form.setAttribute('data-rc-bot-fields', '1');
    var hp = document.createElement('input');
    hp.type = 'text';
    hp.name = 'contact[website]';
    hp.tabIndex = -1;
    hp.setAttribute('autocomplete', 'off');
    hp.setAttribute('aria-hidden', 'true');
    // Off-screen, NOT display:none — naive bots skip display:none fields.
    hp.style.cssText = 'position:absolute!important;left:-9999px!important;top:-9999px!important;height:0;width:0;overflow:hidden;opacity:0;pointer-events:none;';
    form.appendChild(hp);
    var ts = document.createElement('input');
    ts.type = 'hidden';
    ts.name = 'contact[form_render_ts]';
    ts.value = String(Date.now());
    form.appendChild(ts);
  }

  // ensureTurnstile renders an INVISIBLE Cloudflare Turnstile widget on a contact
  // form (mode=invisible, no visible box). execution:'execute' means the token is
  // minted ON DEMAND when refreshTurnstileToken() calls turnstile.execute(id) at
  // submit time — the API-primary flow AWAITS that mint (polls for the token)
  // before POSTing, so the token is always FRESH (avoids the stale-token race
  // that, under ENFORCE with no Shopify fallback, would quarantine a real human
  // and lose the lead). expired/error callbacks reset the widget.
  // Fully defensive: missing sitekey / API not loaded / render throws = NO-OP —
  // it must NEVER break or block the customer's submit.
  function ensureTurnstile(form) {
    try {
      if (!CFG.turnstileSitekey) return;
      if (!window.turnstile || typeof window.turnstile.render !== 'function') return;
      if (form.getAttribute('data-rc-turnstile')) return;
      form.setAttribute('data-rc-turnstile', '1');
      var holder = document.createElement('div');
      holder.style.cssText = 'display:none;';
      form.appendChild(holder);
      var widgetId = window.turnstile.render(holder, {
        sitekey: CFG.turnstileSitekey,
        size: 'invisible',
        execution: 'execute',
        'refresh-expired': 'auto',
        'error-callback': function () { try { window.turnstile.reset(widgetId); } catch (_) {} },
        'expired-callback': function () { try { window.turnstile.reset(widgetId); } catch (_) {} }
      });
      form.setAttribute('data-rc-turnstile-id', widgetId);
    } catch (_) {
      // Render failed — clear the marker so a later pass can retry, never block.
      try { form.removeAttribute('data-rc-turnstile'); } catch (__) {}
    }
  }

  function scanForms() {
    try {
      var forms = document.querySelectorAll('form[action^="/contact"], form[action*="/contact#"]');
      for (var i = 0; i < forms.length; i++) { ensureBotFields(forms[i]); ensureTurnstile(forms[i]); }
    } catch (_) {}
  }

  if (document.readyState !== 'loading') scanForms();
  else document.addEventListener('DOMContentLoaded', scanForms);
  // Sections rendered late (theme editor, AJAX) get the fields too.
  document.addEventListener('shopify:section:load', scanForms);
  // Turnstile's api.js loads async; re-scan a few times so the widget renders
  // once the API is ready, even if it arrives after DOMContentLoaded.
  var rcTsTries = 0;
  var rcTsTimer = setInterval(function () {
    rcTsTries++;
    if (window.turnstile && typeof window.turnstile.render === 'function') { scanForms(); }
    if (rcTsTries >= 20) clearInterval(rcTsTimer); // ~10s ceiling
  }, 500);
})();
